import { Injectable } from '@angular/core';

import { ResponseErrorService } from '../errors/response-error.service';

import { AuthApiProviderService } from '@core/api';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { getExpirationDate, setUserAuth } from 'src/app/core/http/user-auth';
import { FeatureFlagService } from 'src/app/core/services/feature-flag.service';
import { EFeatureFlag } from 'src/app/core/services/feature-flags-consts';
import { clearObjectStores } from '../../helpers/database/database';
import { TLoggedResponse } from '../../view-models/logged-response-model';
import { TLoginResponse } from '../../view-models/login-response-model';
import { TwoFactorAuthenticationStateService } from './2fa/two-factor-authentication-state.service';

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  constructor(
    private responseErrorService: ResponseErrorService,
    private authApiProviderService: AuthApiProviderService,
    private featureFlagService: FeatureFlagService,
    private twoFactorAuthenticationStateService: TwoFactorAuthenticationStateService,
  ) {}

  login({
    email,
    password,
    remember,
    twoFactorAuthCode,
  }: {
    email: string;
    password: string;
    remember: boolean;
    twoFactorAuthCode?: string;
  }): Observable<TLoginResponse> {
    const params = new FormData();

    params.append('email', email);
    params.append('passwordHash', password);

    if (twoFactorAuthCode) {
      params.append('twoFactorAuthCode', twoFactorAuthCode);
    }

    return this.authApiProviderService.login(params).pipe(
      switchMap((response) => this.processLoginResponse(response, remember)),
      catchError((error) => throwError(error)),
    );
  }

  saveToken(authToken: string, email: string, remember: boolean): void {
    const expirationDate = getExpirationDate(remember);

    setUserAuth({ email, token: authToken, expiration: expirationDate, remember });
  }

  private processLoginResponse(
    response: TLoginResponse,
    remember: boolean,
  ): Observable<TLoginResponse> {
    this.saveToken(response.authToken, response.user, remember);
    this.twoFactorAuthenticationStateService.update({
      enabled2fa: response.enabled2fa,
      setup2faAfter: response.setup2faAfter,
    });

    clearObjectStores();

    if (this.featureFlagService.getFlag(EFeatureFlag.REMOVE_LOGGED_ENDPOINT)) {
      return of(response);
    }

    return this.fetchLoggedUser().pipe(map(() => response));
  }

  private fetchLoggedUser(): Observable<TLoggedResponse> {
    return this.authApiProviderService.getLoggedUser().pipe(
      catchError((error) => {
        this.responseErrorService.handleRequestError(
          error,
          'Failed to fetch logged user after login',
        );

        throw error;
      }),
    );
  }
}
